import axios from '@/plugins/axios'
import { context } from '@/services'
import { toUserFriendlyError } from '@/utils/error-message'

const endpoint = '/api-proxy/api/v1/session-calls'

export type SessionCallConnection = {
  callId: number
  sessionId: number
  agoraRoom: string
  agoraToken: string
  agoraUid: string
  expiresAt: string
}

export type SessionCallParticipant = {
  userId: number
  displayName: string
  maqariRoles: Array<'ADMIN' | 'TEACHER' | 'STUDENT'>
  role: 'moderator' | 'participant'
  agoraUid: string
  audioMuted: boolean
}

export type SessionCall = {
  id: number
  sessionId: number
  moderatorUserId: number
  title: string
  roomType: 'private' | 'group'
  status: 'ringing' | 'active' | 'ended' | 'cancelled'
  participants: SessionCallParticipant[]
}

export type SessionCallResponse = {
  call: SessionCall
  connection: SessionCallConnection
}

type CompatibilitySessionRoomParticipant = {
  user_id: number
  student_id: number | false
  teacher_id: number | false
  moderator: boolean
  agora_uid: string
  audio_muted: boolean
  name: string
}

type CompatibilitySessionRoom = {
  id: number
  session_id: number
  room_type: 'private' | 'group'
  agora_room: string
  agora_token: string | false
  status: SessionCall['status']
  user_ids: CompatibilitySessionRoomParticipant[]
}

type CompatibilityResponse<T> = {
  result: T
  error?: { message?: string }
}
function toError(error: unknown) {
  return toUserFriendlyError(error, 'تعذر تنفيذ عملية الاتصال')
}

export async function createSessionCall(sessionId: number, studentId: number) {
  try {
    const { data } = await axios.post<SessionCallResponse>(endpoint, {
      sessionId,
      studentIds: [studentId]
    })
    return data
  } catch (error) {
    throw toError(error)
  }
}

// The legacy Agora room endpoint intentionally creates one room for the
// session's assigned teacher and every active student with an account. Keep
// the transport detail here, while the UI works with the same call model used
// for private calls.
export async function createSessionRoom(sessionId: number): Promise<SessionCallResponse> {
  try {
    const { data } = await axios.post<CompatibilityResponse<CompatibilitySessionRoom>>(
      `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/agora/new`,
      {
        jsonrpc: '2.0',
        params: { session_id: sessionId }
      }
    )

    if (data.error) {
      throw toUserFriendlyError(data.error, 'تعذر بدء مكالمة الحلقة')
    }

    const room = data.result
    if (!room.agora_token) {
      throw new Error('تعذر الحصول على بيانات مكالمة الحلقة')
    }

    const moderator = room.user_ids.find((participant) => participant.moderator)
    if (!moderator) {
      throw new Error('تعذر تحديد مشرف مكالمة الحلقة')
    }

    return {
      call: {
        id: room.id,
        sessionId: room.session_id,
        moderatorUserId: moderator.user_id,
        title: '',
        roomType: room.room_type,
        status: room.status,
        participants: room.user_ids.map((participant) => ({
          userId: participant.user_id,
          displayName: participant.name,
          maqariRoles:
            participant.moderator || participant.teacher_id !== false ? ['TEACHER'] : ['STUDENT'],
          role: participant.moderator ? 'moderator' : 'participant',
          agoraUid: participant.agora_uid,
          audioMuted: participant.audio_muted
        }))
      },
      connection: {
        callId: room.id,
        sessionId: room.session_id,
        agoraRoom: room.agora_room,
        agoraToken: room.agora_token,
        agoraUid: moderator.agora_uid,
        expiresAt: ''
      }
    }
  } catch (error) {
    throw toError(error)
  }
}

export async function answerSessionCall(callId: number) {
  try {
    const { data } = await axios.post<SessionCallResponse>(`${endpoint}/${callId}/answer`)
    return data
  } catch (error) {
    throw toError(error)
  }
}

export async function getSessionCall(callId: number) {
  try {
    const { data } = await axios.get<SessionCall>(`${endpoint}/${callId}`)
    return data
  } catch (error) {
    throw toError(error)
  }
}

export async function renewSessionCallCredentials(callId: number) {
  try {
    const { data } = await axios.post<{ connection: SessionCallConnection }>(
      `${endpoint}/${callId}/credentials`
    )
    return data.connection
  } catch (error) {
    throw toError(error)
  }
}

export async function leaveSessionCall(callId: number) {
  try {
    await axios.post(`${endpoint}/${callId}/leave`)
  } catch (error) {
    throw toError(error)
  }
}

export async function endSessionCall(callId: number) {
  try {
    await axios.post(`${endpoint}/${callId}/end`)
  } catch (error) {
    throw toError(error)
  }
}

/**
 * Applies a teacher moderation action to group-call participants. Omitting
 * userIds applies the action to every non-moderator participant in the call.
 */
export async function setSessionCallParticipantsAudioMuted(
  callId: number,
  muted: boolean,
  userIds?: number[]
) {
  try {
    const { data } = await axios.post<{ call: SessionCall }>(`${endpoint}/${callId}/audio-moderation`, {
      muted,
      ...(userIds?.length ? { userIds } : {})
    })
    return data.call
  } catch (error) {
    throw toError(error)
  }
}
